import { DestroyRef, Directive, effect, ElementRef, inject, input, signal } from '@angular/core';
import { BootstrapService } from '../services/bootstrap.service';

/**
 * Closes the Actions menu on the host `.dropdown` once the action started from it finishes.
 *
 * An item that runs async work stops its own click so its in-menu spinner stays visible, which also
 * stops Bootstrap's document-level handler from closing the menu. Bind this to whatever the item
 * already uses to render that spinner and the menu closes itself when the work settles (DEV-2806).
 */
@Directive({
    selector: '[msCloseMenuOnSettle]'
})
export class CloseMenuOnSettleDirective {

    public isWorking = input.required<boolean>({ alias: 'msCloseMenuOnSettle' });

    private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private bootstrapService = inject(BootstrapService);

    // A signal, not a flag: writing it schedules the effect, so the close is still evaluated when
    // work settles too fast to ever flip isWorking — a cached response never reaching a render.
    private isPending = signal<boolean>(false);

    constructor() {
        effect(() => {
            if (!this.isPending() || this.isWorking()) return;

            this.isPending.set(false);
            this.bootstrapService.hideDropdownIfPresent(this.findToggle());
        });

        // Capture, not bubble: the items this exists for stop their own click, so a bubbling
        // listener on the host would never hear it.
        const host = this.elementRef.nativeElement;
        const onClick = (event: Event) => this.onItemClicked(event);
        host.addEventListener('click', onClick, true);
        inject(DestroyRef).onDestroy(() => host.removeEventListener('click', onClick, true));
    }

    /**
     * Only a menu item's own click arms the close, so a list re-render that re-evaluates the binding
     * at its resting value leaves a menu the user just opened alone.
     */
    private onItemClicked(event: Event): void {
        if (!(event.target as HTMLElement | null)?.closest('.dropdown-item')) return;

        this.isPending.set(true);
    }

    /**
     * The menu may have been portaled onto `<body>`, but the toggle never moves, so it is always
     * still inside the host.
     */
    private findToggle(): HTMLElement | undefined {
        return this.elementRef.nativeElement
            .querySelector<HTMLElement>('[data-bs-toggle="dropdown"]') ?? undefined;
    }

}
